import { Chip, CircularProgress, InputAdornment, Stack, Typography } from '@mui/material';
import { getCampaignStatus, getFormattedChfCurrency } from 'utils';
import React, { useEffect, useState } from 'react';
import { useCampaign, useIsMobile, useNotifications, useStats } from 'hooks';
import { OverviewStatsCard } from './OverviewStatsCard';
import { numberInputStyling } from 'shared';
import { BillingMethod, CampaignState, CampaignStatus, CampaignWithLowPerformance } from 'types';
import { OverviewMaxUnitPriceCard } from './OverviewMaxUnitPriceCard';
import {
  getWeeklyPerformanceColor,
  getWeeklyPerformanceEffectiveRateColor,
  getWeeklyPerformanceEffectiveRateText,
  getWeeklyPerformanceText,
} from './helpers';

const calculateBudgetLeft = (total: number | undefined, spent: number | undefined) => {
  if (!total) return 0;
  if (!spent) return total;

  return total - spent;
};

const CommonFieldInputProps = {
  type: 'number',
  slotProps: {
    input: {
      startAdornment: <InputAdornment position="start">CHF</InputAdornment>,
      sx: {
        backgroundColor: '#fff',
      },
    },
  },
  sx: numberInputStyling,
};

const defaultSuccessMessage = 'Kampagne aktualisiert';

export const OverviewStats = () => {
  const { isMobile } = useIsMobile();
  const { getCampaignQuery, updatePublishedCampaignMutation } = useCampaign();
  const { checkStatsQuery } = useStats();
  const campaign = getCampaignQuery.data?.campaign;
  const stats = checkStatsQuery.data;
  const [successMessage, setSuccessMessage] = useState(defaultSuccessMessage);
  const { setNotification } = useNotifications();
  const shouldShowEngagementCards =
    (campaign?.ga4PropertyId && campaign?.ga4ConnectionValid) || campaign?.billingMethod === BillingMethod.ENGAGEMENT;
  const status = getCampaignStatus({
    state: campaign?.state || CampaignState.draft,
    startDate: campaign?.startDate || null,
    endDate: campaign?.endDate || null,
    isStopped: campaign?.isStopped,
  });

  useEffect(() => {
    if (updatePublishedCampaignMutation.isSuccess) {
      setNotification(successMessage, 'success');
    }
  }, [updatePublishedCampaignMutation.isSuccess, successMessage, setNotification]);

  if (checkStatsQuery.isLoading) {
    return <CircularProgress />;
  }

  return (
    <Stack direction={isMobile ? 'column' : 'row'} flexWrap="wrap" gap={5} spacing={0}>
      <OverviewStatsCard
        title="Übriges Budget"
        initialFieldValue={campaign?.budget}
        value={getFormattedChfCurrency(calculateBudgetLeft(campaign?.budget, stats?.spentBudget))}
        tooltipText={
          <Stack spacing={2}>
            <Typography variant="body2">
              Hier siehst du dein verbleibendes Budget sowie das Gesamtbudget exkl. MwSt.
            </Typography>
            <Typography variant="body2">
              Das Budget wird automatisch über die gesamte Kampagnenlaufzeit verteilt, sodass sichergestellt ist, dass
              es für die gesamte Dauer ausreicht.
            </Typography>
          </Stack>
        }
        secondaryText={`Von ${getFormattedChfCurrency(campaign?.budget)}`}
        inputProps={{
          ...CommonFieldInputProps,
          label: 'Budget',
          disabled: [CampaignStatus.over, CampaignStatus.stopped].includes(status as unknown as CampaignStatus),
        }}
        onSubmit={(value) => {
          setSuccessMessage(defaultSuccessMessage);

          updatePublishedCampaignMutation.mutate({
            payload: {
              budget: Number(value),
              maxUnitPrice: campaign?.maxUnitPrice,
            },
          });
        }}
      />
      {Number.isInteger(stats?.clicks) ? (
        <OverviewStatsCard
          title="Klicks auf Banner/Videos"
          value={stats?.clicks as number}
          tooltipText={
            <Stack spacing={2}>
              <Typography variant="body2">Gesamtanzahl der bisherigen Klicks auf deine Banner oder Videos.</Typography>
            </Stack>
          }
          secondaryText={getWeeklyPerformanceText(stats?.clicksPercentDiff || 0)}
          valuePercentage={`${stats?.clicksPercentDiff || 0}%`}
          {...getWeeklyPerformanceColor(stats?.clicksPercentDiff || 0)}
        />
      ) : null}
      {shouldShowEngagementCards ? (
        <>
          <OverviewStatsCard
            title="Engagements"
            value={stats?.engagements ?? 0}
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">Total der bisherigen Engagements auf deiner Webseite.</Typography>
                <Typography variant="body2" fontWeight="bold">
                  Was zählt als Engagement?
                </Typography>
                <Typography variant="body2">
                  <ul
                    style={{
                      paddingInlineStart: '24px',
                      padding: '0 16px 16px 24px',
                    }}
                  >
                    <li>
                      Ein <b>Conversion Event</b> wurde ausgelöst
                    </li>
                    <li>
                      Oder der User ist für <b>min. 10 Sekunden</b> auf deiner Webseite
                    </li>
                    <li>
                      Oder es gab mindestens <b>2 Pageviews</b>
                    </li>
                  </ul>
                </Typography>
              </Stack>
            }
            secondaryText={getWeeklyPerformanceText(stats?.engagementsPercentDiff || 0)}
            extraChip={
              (campaign as CampaignWithLowPerformance).hasLowPerformance ? (
                <Chip
                  label="Zu tiefe Performance"
                  sx={(theme) => ({
                    width: 'fit-content',
                    backgroundColor: 'warning.light',
                    color: 'warning.dark',
                    height: theme.spacing(6),
                    fontWeight: 'bold',
                    marginLeft: theme.spacing(1),
                    border: '1px solid',
                    borderColor: 'warning.main',
                  })}
                />
              ) : undefined
            }
            valuePercentage={`${stats?.engagementsPercentDiff || 0}%`}
            {...getWeeklyPerformanceColor(stats?.engagementsPercentDiff || 0)}
          />
          <OverviewStatsCard
            title="Engagement Rate"
            value={`${stats?.engagementRatePercent?.toFixed(stats?.engagementRatePercent !== 0 ? 1 : 0) ?? 0}%`}
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Die Engagement Rate misst das Verhältnis zwischen Engagements und Klicks. Sie zeigt, wie viele der
                  Klicks in Engagements umgewandelt wurden: Engagements ÷ Klicks × 100.
                </Typography>
              </Stack>
            }
            secondaryText={getWeeklyPerformanceText(stats?.engagementRatePercentDiff ?? 0)}
            valuePercentage={`${stats?.engagementRatePercentDiff?.toFixed(stats?.engagementRatePercentDiff !== 0 ? 1 : 0) ?? 0}%`}
            {...getWeeklyPerformanceColor(stats?.engagementRatePercentDiff ?? 0)}
          />
        </>
      ) : null}
      {campaign?.billingMethod === BillingMethod.ENGAGEMENT ? (
        <>
          <OverviewStatsCard
            title="Effektive Kosten pro Engagement"
            value={`${getFormattedChfCurrency(stats?.costPerEngagement)}`}
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Soviel hat bisher ein einzelnes Engagement im Tagesdurchschnitt gekostet.
                </Typography>
              </Stack>
            }
            secondaryText={getWeeklyPerformanceEffectiveRateText(stats?.costPerEngagementPercentDiff || 0)}
            valuePercentage={`${stats?.costPerEngagementPercentDiff || 0}%`}
            isExtended
            {...getWeeklyPerformanceEffectiveRateColor(stats?.costPerEngagementPercentDiff || 0)}
          />
          <OverviewMaxUnitPriceCard
            title="Max. Preis pro Engagement"
            value={
              Number.isInteger(campaign.maxUnitPrice)
                ? `${getFormattedChfCurrency(campaign.maxUnitPrice)}`
                : (campaign.maxUnitPrice as null)
            }
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Alloy sucht immer den günstigsten Preis für dich – unabhängig davon, ob du einen Maximalpreis angibst
                  oder nicht. Mit einem Maximalpreis zahlst du nie mehr als diesen Betrag pro Engagement.
                </Typography>
                <Typography variant="body2">
                  Falls die Engagements zu niedrig ausfallen (z. B. durch einen zu niedrigen Maximalpreis), wirst du per
                  E-Mail informiert – inklusive Tipps zur Verbesserung der Performance.
                </Typography>
              </Stack>
            }
            priceModelText="Preis pro Engagement"
            priceModelTooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Die Kampagne wird auf Basis der Anzahl an Engagements auf der Webseite abgerechnet – unter
                  Berücksichtigung des Gesamtbudgets.
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  Was zählt als Engagement?
                </Typography>
                <Typography variant="body2">
                  <ul
                    style={{
                      paddingInlineStart: '24px',
                      padding: '0 16px 16px 24px',
                    }}
                  >
                    <li>
                      Ein <b>Conversion Event</b> wurde ausgelöst
                    </li>
                    <li>
                      Oder der User ist für <b>min. 10 Sekunden</b> auf deiner Webseite
                    </li>
                    <li>
                      Oder es gab mindestens <b>2 Pageviews</b>
                    </li>
                  </ul>
                </Typography>
              </Stack>
            }
            inputProps={{
              ...CommonFieldInputProps,
              label: 'Max. Preis pro Engagement',
              disabled: [CampaignStatus.over, CampaignStatus.stopped].includes(status as unknown as CampaignStatus),
              helperText:
                (campaign as CampaignWithLowPerformance).hasLowPerformance && campaign?.effectiveCostPerUnit ? (
                  <Typography variant="body2" color="warning.dark">
                    <b>Hinweis:</b> Ein Preis unter {getFormattedChfCurrency(campaign?.effectiveCostPerUnit)} könnte die
                    Performance einschränken. Dieser Wert wurde durch das bisherige Tracking deiner Kampagne berechnet.
                  </Typography>
                ) : undefined,
            }}
            initialFieldValue={campaign?.maxUnitPrice ?? ''}
            onSubmit={(value) => {
              setSuccessMessage(
                (campaign as CampaignWithLowPerformance).hasLowPerformance
                  ? 'Änderung erfolgreich gespeichert. Es kann bis zu 48 Stunden dauern, bis die verbesserte Performance sichtbar wird.'
                  : defaultSuccessMessage
              );

              updatePublishedCampaignMutation.mutate({
                payload: {
                  budget: campaign?.budget,
                  maxUnitPrice: value === null ? value : Number(value),
                },
              });
            }}
            infoMessage={
              (campaign as CampaignWithLowPerformance).hasLowPerformance &&
              Number.isInteger(campaign.effectiveCostPerUnit) ? (
                <>
                  <Typography variant="body2">
                    <b>Entferne den Max. Preis pro Engagement</b> oder erhöhe ihn auf{' '}
                    <b>mindestens {getFormattedChfCurrency(campaign.effectiveCostPerUnit)}</b>.– um die Performance zu
                    verbessern.
                  </Typography>
                  <br />
                  <Typography variant="body2">
                    Dieser Wert wurde durch das bisherige Tracking deiner Kampagne berechnet.
                  </Typography>
                </>
              ) : null
            }
          />
        </>
      ) : null}
      {campaign?.billingMethod === BillingMethod.CLICK ? (
        <>
          <OverviewStatsCard
            title="Effektive Kosten pro Klick"
            isExtended={shouldShowEngagementCards}
            value={`${getFormattedChfCurrency(stats?.costPerClick)}`}
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">Soviel hat ein einzelner Klick im Durchschnitt gekostet.</Typography>
              </Stack>
            }
            secondaryText={getWeeklyPerformanceEffectiveRateText(stats?.costPerClickPercentDiff || 0)}
            valuePercentage={`${stats?.costPerClickPercentDiff || 0}%`}
            {...getWeeklyPerformanceEffectiveRateColor(stats?.costPerClickPercentDiff || 0)}
          />
          <OverviewMaxUnitPriceCard
            title="Max. Preis pro Klick"
            value={
              Number.isInteger(campaign.maxUnitPrice)
                ? `${getFormattedChfCurrency(campaign.maxUnitPrice)}`
                : (campaign.maxUnitPrice as null)
            }
            tooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Alloy sucht immer den günstigsten Preis für dich – unabhängig davon, ob du einen Maximalpreis angibst
                  oder nicht. Mit einem Maximalpreis zahlst du nie mehr als diesen Betrag pro Klick.
                </Typography>
                <Typography variant="body2">
                  Falls die Klicks zu niedrig ausfallen (z. B. durch einen zu niedrigen Maximalpreis), wirst du per
                  E-Mail informiert – inklusive Tipps zur Verbesserung der Performance.
                </Typography>
              </Stack>
            }
            priceModelText="Preis pro Klick"
            priceModelTooltipText={
              <Stack spacing={2}>
                <Typography variant="body2">
                  Die Kampagne wird auf Basis der Anzahl Klicks auf Banner oder Videos abgerechnet – unter
                  Berücksichtigung des Gesamtbudgets.
                </Typography>
              </Stack>
            }
            inputProps={{
              ...CommonFieldInputProps,
              label: 'Max. Preis pro Klick',
              disabled: [CampaignStatus.over, CampaignStatus.stopped].includes(status as unknown as CampaignStatus),
            }}
            initialFieldValue={campaign?.maxUnitPrice ?? ''}
            onSubmit={(value) => {
              setSuccessMessage(defaultSuccessMessage);

              updatePublishedCampaignMutation.mutate({
                payload: {
                  budget: campaign?.budget,
                  maxUnitPrice: value === null ? value : Number(value),
                },
              });
            }}
          />
        </>
      ) : null}
    </Stack>
  );
};
